import Spinner from "@material-tailwind/react/components/Spinner";
import { Button as MuiButton } from "@material-tailwind/react/components/Button";
import { ReactNode, forwardRef, memo, useMemo } from "react";
import classNames from "classnames";
import type { ButtonProps } from "@material-tailwind/react/components/Button";
import { TTypographyProps } from "@/components/kit/Typography";
import { Typography } from "@/components/kit";

type TProps = {
  children: ReactNode | string;
  isLoading?: boolean;
  size?: ButtonProps["size"] | "xlg" | "xs" | "smRegular";
};

export type Ref = HTMLButtonElement;

export type TButtonProps = Omit<ButtonProps, "loading" | "ref" | "size"> &
  TProps;
const Button = forwardRef<Ref, TButtonProps>((props, ref) => {
  const { isLoading, children, className, size = "md", ...rest } = props;
  const maskedChildren = () => {
    if (typeof children !== "string") return children;
    return children.charAt(0).toUpperCase() + children.slice(1);
  };

  const typographyVariant = useMemo(() => {
    const size = props.size || "md";
    const result: Record<
      NonNullable<TProps["size"]>,
      TTypographyProps["variant"]
    > = {
      xs: "Caption1-Bold",
      sm: "Body2-Bold",
      md: "BodyPlus1-bold",
      smRegular: "Body2-Regular",
      lg: "Title5-Bold",
      xlg: "Title5-Bold",
    };
    return result[size];
  }, [props.variant]);
  return (
    <MuiButton
      placeholder=""
      className={classNames(
        {
          "!py-4 !px-6 !text-[20px]": size === "xlg",
          "!h-[48px]": size === "lg",
          "!h-[40px]": size === "md",
          "!h-[32px]": size === "sm",
          "!h-6 !py-[10px] !px-2 !text-[12px]": size === "xs",
        },
        className,
      )}
      {...rest}
      ref={ref}
      ripple={false}
      disabled={isLoading || rest.disabled}
    >
      <Typography
        variant={typographyVariant}
        className={classNames(
          `flex items-center h-full w-full gap-1 justify-center whitespace-nowrap normal-case !text-inherit`,
        )}
      >
        {maskedChildren()}
        {isLoading && <Spinner className="h-4 w-4" />}
      </Typography>
    </MuiButton>
  );
});

export default memo(Button);
